import axios from 'axios';
import {flow, getEnv, onPatch, types} from 'mobx-state-tree';
import * as api from '../services/api';
import {SendBirdAction, SendBirdConnection} from '../services/sb';
import {
  clearAccessToken,
  getAccessToken,
  saveAccessToken
} from '../services/tokenPersistence';
import {isJwtExpired} from '../utils';
import withFeedbackModel, {
  WithFeedbackActions
} from './composables/withFeedbackModel';

const LS_ALL_ROLES_KEY = 'all_roles';

function getRoleId(roleCode: string) {
  const stringifiedRoles = localStorage.getItem(LS_ALL_ROLES_KEY);
  const allRoles = JSON.parse(stringifiedRoles!);
  // tslint:disable-next-line:no-any
  return allRoles.filter((x: any) => x.code === roleCode)[0].id;
}

export const UserRoleModel = types.model({
  id: types.number,
  code: types.identifier,
  name: types.string
});

export type UserRole = typeof UserRoleModel.Type;

export const UserProfileModel = types.model({
  id: types.identifierNumber,
  auth0UserId: types.string,
  avatar: types.string,
  email: types.string,
  firstName: types.string,
  lastName: types.string,
  fullName: types.string,
  createdAt: types.number,
  sendbirdUserId: types.string,
  role: UserRoleModel
});

export type UserProfile = typeof UserProfileModel.Type;

export const UserRegistrationFieldsModel = types.model({
  email: types.optional(types.string, ''),
  firstName: types.optional(types.string, ''),
  lastName: types.optional(types.string, ''),
  password: types.optional(types.string, ''),
  location: types.union(types.string, types.undefined)
});

export type UserRegistrationFields = typeof UserRegistrationFieldsModel.Type;

export const UserRegistrationModel = types.compose(
  types
    .model({
      currentStepIndex: types.optional(types.number, 0),
      sending: types.optional(types.boolean, false),
      resendingCode: types.optional(types.boolean, false),
      verifying: types.optional(types.boolean, false),
      verificationStatus: types.optional(
        types.model({
          processed: types.boolean,
          success: types.boolean
        }),
        {processed: false, success: false}
      ),
      error: types.maybeNull(types.string)
    })
    .views(self => ({
      get verificationProcessed() {
        return self.verificationStatus.processed;
      },
      get verificationPassed() {
        return (
          self.verificationStatus.processed && self.verificationStatus.success
        );
      },
      get verificationFailed() {
        return (
          self.verificationStatus.processed && !self.verificationStatus.success
        );
      }
    }))
    .actions(self => ({
      verified: function() {
        self.verificationStatus = {processed: true, success: true};
      },
      failedVerification: function() {
        self.verificationStatus = {processed: true, success: false};
      }
    })),
  UserRegistrationFieldsModel
);

export type UserRegistration = typeof UserRegistrationModel.Type;

export enum AuthError {
  CHANGE_PWD_CURRENT_PWD_INVALID = 'CHANGE_PWD_CURRENT_PWD_INVALID'
}

export type AuthModelActions = {
  sendRegistration: (fields: UserRegistrationFields) => void;
  resendVerificationCode: () => void;
  verifyRegistration: (code: string, roleCode: string) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  restoreLastLogin: (persistedToken: string) => Promise<void>;
  fetchUserInfo: () => Promise<void>;
  afterCreate: () => void;
  changePassword: (currentPassword: string, newPassword: string) => void;
  forgotPassword: (email: string) => void;
  resetPassword: (
    token: string,
    password: string,
    redirect: () => void
  ) => void;
  addSendbirdConnectionHandler: () => void;
};

const AuthModel = types.compose(
  withFeedbackModel,
  types
    .model({
      profile: types.maybeNull(UserProfileModel),
      jwt: types.maybeNull(types.string),
      registration: types.optional(UserRegistrationModel, {}),
      isLoggingIn: types.optional(types.boolean, false),
      isLoggedIn: types.optional(types.boolean, false),
      isChangingPassword: types.optional(types.boolean, false),
      isRequestingPasswordReset: types.optional(types.boolean, false),
      isResettingPassword: types.optional(types.boolean, false),
      isFirstTimeUser: types.optional(types.boolean, false),
      errors: types.map(types.boolean)
    })
    .actions<AuthModelActions>(self => {
      // tslint:disable-next-line:no-any
      const withActions = (self as any) as AuthModelActions;

      // tslint:disable-next-line:no-any
      const withFeedback = (self as any) as WithFeedbackActions;

      const {appFeedback: feedback} = getEnv(self);

      const sb = new SendBirdAction();
      const connectionManager = new SendBirdConnection();

      return {
        sendRegistration: flow(function*(fields: UserRegistrationFields) {
          self.registration.sending = true;

          const response = yield api.sendEmailCode(fields.email);

          if (response.status === 201) {
            self.registration = {
              ...fields,
              currentStepIndex: 1
            } as UserRegistration;
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.registration.sending = false;
        }),

        resendVerificationCode: flow(function*() {
          self.registration.resendingCode = true;
          yield api.sendEmailCode(self.registration.email);
          self.registration.resendingCode = false;
        }),

        verifyRegistration: flow(function*(code: string, roleCode: string) {
          self.registration.verifying = true;

          let response = yield api.verifyEmailCode(
            self.registration.email,
            code.trim()
          );

          if (response.status === 200) {
            self.registration.verified();
            withFeedback.sendSuccessFeedback('Verified successfully');

            const {email, password, firstName, lastName} = self.registration;
            response = yield api.signup(
              email,
              password,
              firstName,
              lastName,
              getRoleId(roleCode)
            );

            if (response.data && response.status === 201) {
              self.isFirstTimeUser = true;

              withFeedback.sendSuccessFeedback(
                'Your account was successfully created'
              );

              yield withActions.login(email, password);
            }

            if (response.status >= 400) {
              withFeedback.sendErrorFeedback();
            }
          }

          if (response.status === 400) {
            self.registration.failedVerification();
          }

          if (response.status > 400) {
            withFeedback.sendErrorFeedback();
          }

          self.registration.verifying = false;
        }),

        logout: function() {
          self.jwt = null;

          // FIXME: For now just force redirect to clear stores conveniently

          // self.isLoggedIn = false;

          // tslint:disable-next-line:no-any
          (window as any).location = '/';
        },

        login: flow(function*(email: string, password: string) {
          self.isLoggingIn = true;

          const response = yield api.login(email, password);

          if (response && response.data && response.status === 200) {
            const {user, accessToken} = response.data;

            self.jwt = accessToken;
            self.isLoggedIn = true;
            self.profile = user;

            withFeedback.sendSuccessFeedback('Login successfully.');
          } else if (response.status === 401) {
            withFeedback.sendErrorFeedback(
              'Invalid login: Wrong email/password combination.'
            );
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.isLoggingIn = false;
        }),

        restoreLastLogin: flow(function*(accessToken: string) {
          self.jwt = accessToken;
          self.isLoggedIn = true;
          yield withActions.fetchUserInfo();
        }),

        fetchUserInfo: flow(function*() {
          const response = yield api.fetchUserInfo();

          if (response.data && response.status === 200) {
            self.profile = response.data.user;
          } else {
            withFeedback.sendErrorFeedback(
              'An error has occurred as we tried to load your profile.'
            );
          }
        }),

        afterCreate: flow(function*() {
          // TODO: maybe find a cleaner way to inject auth token? This is very convenient though.
          onPatch(self, ({path, value}) => {
            if (path === '/jwt') {
              if (value) {
                axios.defaults.headers.common.Authorization = `Bearer ${value}`;
                saveAccessToken(value);
              } else {
                delete axios.defaults.headers.common.Authorization;
                clearAccessToken();
              }
            }

            if (path === '/profile') {
              if (value.sendbirdUserId) {
                sb.connect(value.sendbirdUserId)
                  .then(() => {
                    withActions.addSendbirdConnectionHandler();
                  })
                  .catch(() => {
                    // tslint:disable-next-line:no-console
                    console.log(
                      'ChatChannelModel Error: SendBird connection failed.'
                    );
                  });
              }
            }
          });

          const persistedToken = getAccessToken();

          try {
            if (persistedToken && !isJwtExpired(persistedToken)) {
              yield withActions.restoreLastLogin(persistedToken);
            } else {
              clearAccessToken();
            }
          } catch {
            clearAccessToken();
          }

          const getRoles = yield api.getRoles();

          if (getRoles && getRoles.data && getRoles.status === 200) {
            localStorage.setItem(
              LS_ALL_ROLES_KEY,
              JSON.stringify(getRoles.data.roles)
            );
          }
        }),

        changePassword: flow(function*(
          currentPassword: string,
          newPassword: string
        ) {
          self.isChangingPassword = true;

          const response = yield api.changePassword(
            currentPassword,
            newPassword
          );

          if (response.status === 200) {
            withFeedback.sendSuccessFeedback('Password successfully changed.');
          } else if (response.status === 401) {
            self.errors.set(AuthError.CHANGE_PWD_CURRENT_PWD_INVALID, true);
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.isChangingPassword = false;
        }),

        forgotPassword: flow(function*(email: string) {
          self.isRequestingPasswordReset = true;

          const response = yield api.forgotPassword(email);

          if (response.status === 201) {
            feedback.add({
              uid: `open${Date.now()}`,
              message:
                'Success!. If an account associated with the email you entered exists in our system,' +
                ' password reset instructions will be sent to your email.',
              intent: 'success',
              duration: 10000
            });
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.isRequestingPasswordReset = false;
        }),

        resetPassword: flow(function*(
          token: string,
          password: string,
          redirect: () => void
        ) {
          self.isResettingPassword = true;

          const response = yield api.resetPassword(token, password);

          if (response.status === 200) {
            withFeedback.sendSuccessFeedback('Password successfully changed.');
            redirect();
          } else if (response.status === 401) {
            withFeedback.sendErrorFeedback(
              'Invalid request. Please use the "Forgot password" feature again to get a new reset URL.'
            );
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.isResettingPassword = false;
        }),

        addSendbirdConnectionHandler: function() {
          connectionManager.onReconnectStarted = () => {
            // tslint:disable-next-line:no-console
            console.log('[SendBird JS SDK] Reconnect : Started');
          };
          connectionManager.onReconnectSucceeded = () => {
            // tslint:disable-next-line:no-console
            console.log('[SendBird JS SDK] Reconnect : Succeeded');
          };
          connectionManager.onReconnectFailed = () => {
            // tslint:disable-next-line:no-console
            console.log('[SendBird JS SDK] Reconnect : Failed');
          };
        }
      };
    })
);

export type Auth = typeof AuthModel.Type;

export default AuthModel;



// WEBPACK FOOTER //
// ./src/models/authModel.ts